import {
    FareComponentInBookingType,
    FareFamilyType,
    PriceDetailInBookingType,
    PriceInOfferType,
    RateInBookingType,
    ResponseForConvertOfferAfx2jsonType
} from '@afidium/ts-facade';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {IconDefinition} from '@fortawesome/fontawesome-common-types';
import {SizeProp} from '@fortawesome/fontawesome-svg-core';
import {faCaretDown, faCheck, faEuroSign} from '@fortawesome/free-solid-svg-icons';
import {IqbaPathSegment} from 'app_locobook/iqba-paths';
import {PropalService} from 'app_locobook/service/propal/propal.service';
import {QuotedAvailabilityService} from 'app_locobook/service/quoted-availability.service';
import {
    FareFamilyDescriptionDialogComponent,
    IFareFamilyDialogData
} from 'libraries/component/fare-family/fare-family-description-dialog.component';
import {ILoader} from 'libraries/component/loader/ILoader';
import {IStatusColorationRule} from 'libraries/component/status-tag/status-tag.component';
import {INotification, NotificationType} from 'libraries/interfaces/INotification';
import {Yii} from 'libraries/interfaces/Yii';
import {ResourceError} from 'libraries/service/resource/ResourceError';
import {IFareFamilyDictionary} from 'libraries/service/resources/airTrip/IGetAirTripQuotes';
import {BtnStyleTypes} from 'libraries/ui_button/btn.component';
import {Observable, of} from 'rxjs';
import {
    ECellType,
    FareFamilyTableService,
    IFareFamilyTable,
    IFareFamilyTableColumn,
    IFareFamilyTableRow,
    IFareFamilyTableRowData
} from './fare-family-table.service';
import {ErrorTracingService} from "libraries/service/error-tracing/error-tracing.service";
import {PriceSwitcherService} from "../../../../../libraries/component/tab-navigation/price-switcher.service";
import {EXRateType} from "../../../../../libraries/interfaces/ITotal";
import {JourneyXRefType} from "@afidium/ts-facade/model/journeyXRefType";

enum EServiceApplyRule{
    Included = "Included",
    Chargeable = "Chargeable"
}

export interface IOnAlternativeSelectOutput{
    quoteId: string;
    quote: ResponseForConvertOfferAfx2jsonType;
    offerAlternativePriceXId: string;
    fareFamilyXId: string;
}

class ComponentWording{
    public readonly visualizeFareFamiliesDetail: string = Yii.t('wui-iqba-js', "(Voir les textes)");
}

@Component({
    selector: 'fare-family-benchmark',
    template: `
    <ng-container *ngIf="notification.active || gqaLoader.active">
        <notification *ngIf="notification.active"
            [type]="notification.type">{{ notification.message }}</notification>
        <loader *ngIf="gqaLoader.active" [message]="gqaLoader.message"></loader>
    </ng-container>
    <div class="table-container"
        *ngIf="dataSource"
        [style.width]="parentWidth">
        <table mat-table [dataSource]="dataSource.rows">
            <ng-container *ngFor="let column of dataSource.columns; let first = first;">
                <div [matColumnDef]="column.key" [sticky]="first">
                    <th mat-header-cell *matHeaderCellDef
                        [class.is-selected]="column.key === fareFamilySelectionXId"
                        [class.first-column-cell]="first"
                        [class.unlimit-first-column-width]="first"
                        (click)="redirectToSelectedQuote($event, column.altenativeOfferXId, column.key)">
                        {{ column.label }}
                        <btn *ngIf="first"
                            [label]="wording.visualizeFareFamiliesDetail"
                            [type]="btnStyleTypes.actionLink"
                            (onClick)="visualizeFareFamiliesDetail($event)"></btn>
                        <div class="caret-icon-container">
                            <fa-icon
                                class="caret-icon"
                                [class.is-selected]="column.key === fareFamilySelectionXId"
                                [size]="faCaretDownProps.size"
                                [icon]="faCaretDownProps.icon"></fa-icon>
                        </div>
                    </th>

                    <td mat-cell
                        *matCellDef="let element; let i = index;"
                        [ngSwitch]="element[column.key]?.type"
                        [class.first-column-cell]="first">
                        <div [class.first-column]="column.key === 'LINE_NAMES_COLUMN'">
                            <div *ngSwitchCase="'service'" class="td-service">
                                <span *ngIf="element[column.key].data.value" class="status-container">
                                    <status-tag
                                        [icons]="icons"
                                        [statusColorationRule]="statusColorationRule"
                                        [status]="element[column.key].data.value">
                                    </status-tag>
                                    <span *ngIf="element[column.key].data?.quantity" class="quantity">{{ element[column.key].data.quantity || null }}</span>
                                </span>
                            </div>
                            <div *ngSwitchCase="'price'" class="td-price">
                                <ng-container *ngIf="element[column.key].data.value">
                                    <price
                                        [value]="element[column.key].data.value"
                                        [currency]="element[column.key].data.currencyCode">
                                    </price>
                                    <delta-price classList="center-price"
                                        [oldPrice]="selectedFareFamily.price"
                                        [price]="getCurrentPriceForDelta(element[column.key].data)"
                                        [currency]="selectedFareFamily.currency"></delta-price>
                                </ng-container>
                            </div>
                            <div *ngSwitchDefault class="flex">
                                {{ element[column.key].data.value }}
                            </div>
                        </div>
                    </td>
                </div>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
        </table>
    </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ["./fare-family-benchmark.component.scss"]
})

export class FareFamilyBenchmarkComponent implements OnInit, OnChanges {

    @Input()
    public fareFamilySelectionXId: string;
    @Input()
    public sessionPostOnUpdateBooking: any = null;
    @Input()
    public parentWidth: string = "800px";
    @Input()
    public quoteId: string;
    @Input()
    public selectedJourneys: string[];
    @Input()
    public fareFamilyDictionary: { [key: string]: IFareFamilyDictionary };
    @Input()
    public segments: string[];
    @Input()
    public sqaId: string;
    @Input()
    public mustRedirectAfterSelection: boolean = true;

    @Output()
    public readonly onAlternativeSelect: EventEmitter<IOnAlternativeSelectOutput> = new EventEmitter<IOnAlternativeSelectOutput>();
    @Output()
    public readonly onAlternativeOpen: EventEmitter<IOnAlternativeSelectOutput> = new EventEmitter<IOnAlternativeSelectOutput>();

    public readonly faCaretDownProps: {
        icon: IconDefinition,
        size: SizeProp
    } = {
        icon: faCaretDown,
        size: "2x"
    };
    public readonly btnStyleTypes: typeof BtnStyleTypes = BtnStyleTypes;
    public readonly wording: ComponentWording = new ComponentWording();
    public readonly icons: {[status: string]: IconDefinition} = {
        [EServiceApplyRule.Included]: faCheck,
        [EServiceApplyRule.Chargeable]: faEuroSign,
    };
    public readonly statusColorationRule: IStatusColorationRule = {green: EServiceApplyRule.Included};

    public displayedColumns: string[] = [];
    public selectedFareFamily: {
        xId: string,
        price: { [key: string]: number; };
        currency: { code: string; decimals: number; }
    };
    public fareFamilyDialogData: IFareFamilyDialogData[];
    public getQuotedAvailabilityCache: ResponseForConvertOfferAfx2jsonType;
    public dataSource: IFareFamilyTable;
    public selectableFareFamilies: Map<string, boolean>;
    public notification: INotification = {
        active: false,
        type: NotificationType.Error
    };
    public gqaLoader: ILoader = {
        active: false,
        message: Yii.t('wui-iqba-js', "Chargement des autres offres en cours...")
    };
    public selectedPriceType: EXRateType;


    constructor(
        private readonly fareFamilyTableService: FareFamilyTableService,
        private readonly router: Router,
        private readonly propalService: PropalService,
        private readonly quotedAvailabilityService: QuotedAvailabilityService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly matDialog: MatDialog,
        private readonly errorTracingService: ErrorTracingService,
        protected readonly priceSwitcherService: PriceSwitcherService,
    ){}


    public ngOnInit(): void{
        this.initTable();
        this.selectedPriceType = this.priceSwitcherService.selectedPrice || EXRateType.cost;
        this.priceSwitcherService.selectPriceEvent.subscribe((type: EXRateType): void => {
            this.selectedPriceType = type;
            this.initTable();
        });
    }
    public ngOnChanges(simpleChanges: SimpleChanges): void {
        if (simpleChanges.fareFamilySelectionXId?.currentValue && !simpleChanges.fareFamilySelectionXId.firstChange){
            this.initTable();
        }
        this.selectedPriceType = this.priceSwitcherService.selectedPrice || EXRateType.cost;
    }

    public getCurrentPriceForDelta(data: IFareFamilyTableRowData): { [key: string]: number; }{
        return { [data.currencyCode]: data.value as number };
    }

    public async redirectToSelectedQuote(event: Event, alternativeXId: string, fareFamilyXId: string): Promise<void>{
        event.stopPropagation();
        if (this.selectableFareFamilies.get(fareFamilyXId)){
            if (this.mustRedirectAfterSelection) {
                sessionStorage.setItem('$post', JSON.stringify(this.sessionPostOnUpdateBooking));
                await this.router
                    .navigate([
                        IqbaPathSegment.Locobook,
                        IqbaPathSegment.Search,
                        this.sqaId,
                        this.quoteId,
                        IqbaPathSegment.Alternative,
                        alternativeXId])
                    .then((): void => {
                        this.onAlternativeSelect.emit({
                            quoteId: this.quoteId,
                            quote: this.getQuotedAvailabilityCache,
                            offerAlternativePriceXId: alternativeXId,
                            fareFamilyXId
                        });
                    });
            } else {
                this.onAlternativeSelect.emit({
                    quoteId: this.quoteId,
                    quote: this.getQuotedAvailabilityCache,
                    offerAlternativePriceXId: alternativeXId,
                    fareFamilyXId
                });
            }
        } else {
            this.notification.active = true;
            this.notification.type = NotificationType.Warning;
            this.notification.message = Yii.t('wui-iqba-js', "Les offres qui combinent des Fare Families ne peuvent pour le moment pas être sélectionnées.");
        }
    }

    public visualizeFareFamiliesDetail(event: Event): void{
        event.stopPropagation();
        this.matDialog
            .open(FareFamilyDescriptionDialogComponent, {
                minWidth: "800px",
                maxWidth: "90%",
                data: this.fareFamilyDialogData
            });
    }

    private initTable(): void {
        this.propalService.setPropalDisplay(this.segments, this.selectedJourneys);
        this.notification.active = false;
        this.gqaLoader.active = true;
        const gqaSteam: Observable<ResponseForConvertOfferAfx2jsonType> = this.getQuotedAvailabilityCache ?
            of(this.getQuotedAvailabilityCache) :
            this.quotedAvailabilityService.getQuotedAvailabilityStream(this.quoteId, this.selectedJourneys, null, this.sessionPostOnUpdateBooking);
        gqaSteam
            .subscribe({
                next: (response: ResponseForConvertOfferAfx2jsonType): void => {
                    const costDetailRates: RateInBookingType[] = [];
                    const journeyArray: JourneyXRefType[][] = [];
                    response.offer.priceArray.forEach((priceElement: PriceInOfferType): void => {
                        if(priceElement.cost){
                            priceElement.cost.detailArray.forEach((detailCostElement: PriceDetailInBookingType): void => {
                                if(detailCostElement.rate){
                                    costDetailRates.push(detailCostElement.rate);
                                }
                                if(detailCostElement.journeyArray){
                                    journeyArray.push(detailCostElement.journeyArray);
                                }
                            });
                        }
                        if(priceElement.retail){
                            priceElement.retail.detailArray.forEach((detailRetailElement: PriceDetailInBookingType, index: number): void => {
                                priceElement.retail.detailArray[index].rate = costDetailRates[index];
                                priceElement.retail.detailArray[index].journeyArray = journeyArray[index];
                            });
                        }
                    });
                    // if no fareFamily selection xId is provided, take the FF related to the root offer
                    // https://projets2.afidium.com/issues/34624
                    if(!this.fareFamilySelectionXId){
                        const transportationPrice: PriceInOfferType = response.offer.priceArray.find((priceItem: PriceInOfferType): boolean => priceItem.cost.xRole === 'Transportation');
                        const transportationFareComponent: FareComponentInBookingType = transportationPrice.cost.detailArray[0].rate.fareComponentArray
                            .find((item: FareComponentInBookingType): boolean => Boolean(item.fareFamily));
                        this.fareFamilySelectionXId = transportationFareComponent.fareFamily.xRef;

                    }

                    this.getQuotedAvailabilityCache = response;
                    this.gqaLoader.active = false;
                    this.dataSource = this.fareFamilyTableService.getDataSource(response, this.selectedPriceType === EXRateType.retail);
                    this.displayedColumns = this.fareFamilyTableService.getDisplayedColumns(response);
                    this.selectableFareFamilies = this.fareFamilyTableService.getSelectableFareFamilies(this.displayedColumns, response);

                    this.getSelectedFareFamily();
                    this.initFareFamilyDialogData();
                    this.changeDetectorRef.detectChanges();
                    const selectedColumn: IFareFamilyTableColumn = this.dataSource?.columns?.find((column: IFareFamilyTableColumn): boolean => column.key === this.fareFamilySelectionXId);
                    if (selectedColumn) {
                        this.onAlternativeOpen.emit({
                            quoteId: this.quoteId,
                            quote: response,
                            offerAlternativePriceXId: selectedColumn?.altenativeOfferXId,
                            fareFamilyXId: this.fareFamilySelectionXId,
                        });
                    }
                },
                error: (error: ResourceError): void => {
                    this.notification.active = true;
                    this.notification.type = NotificationType.Error;
                    this.notification.message =
                        `${Yii.t('wui-iqba-js', "Une erreur est survenue")}${error.message?.length > 0 ? ' : ' : ''}${error.message}`;
                    this.gqaLoader.active = false;
                    this.errorTracingService.captureException(error);
                    this.changeDetectorRef.detectChanges();
                }
            });
    }

    private initFareFamilyDialogData(): void{
        const [LINE_NAMES_COLUMN, ...availableFareFamily]: string[] = this.displayedColumns;
        this.fareFamilyDialogData = this.getQuotedAvailabilityCache.dataList?.fareFamilyArray?.map((dictionary: FareFamilyType): IFareFamilyDialogData => {
                const rowsWithPrice: IFareFamilyTableRow = this.dataSource.rows
                    .find((row: IFareFamilyTableRow): boolean =>
                        Object.entries(row)
                            .some(([key, {type}]: [string, { type: ECellType }]): boolean => type === ECellType.Price)
                    );
                return {
                    dictionary,
                    isSelected: dictionary.xId === this.fareFamilySelectionXId,
                    price: rowsWithPrice ?
                        rowsWithPrice[dictionary.xId].data :
                        null
                };
            })
            .filter((fareFamilyDialogData: IFareFamilyDialogData): boolean =>
                availableFareFamily.some((fareFamilyCode: string): boolean => fareFamilyCode === fareFamilyDialogData.dictionary.xId)
            );
        }

    private getSelectedFareFamily(): void{
        if(this.dataSource?.rows?.length){
            if(this.dataSource.rows[0][this.fareFamilySelectionXId] ){
                const { currencyCode, value, decimals}: IFareFamilyTableRowData = this.dataSource.rows[0][this.fareFamilySelectionXId].data
                this.selectedFareFamily = {
                    xId: this.fareFamilySelectionXId,
                    price: { [currencyCode]: value as number},
                    currency: { code: currencyCode, decimals}
                };
            }else{
                this.selectedFareFamily = {
                    xId: this.fareFamilySelectionXId,
                    price: null,
                    currency: null,
                };
            }
        }else{
            this.selectedFareFamily = {
                xId: this.fareFamilySelectionXId,
                price: null,
                currency: null,
            };
        }
    }
}
